/**
 * Wind as an arrow with a number, coloured by strength.
 *
 * Three reports in three minutes said the same thing: barbs are the wrong
 * presentation here (fb-msgjg0hs-0, fb-msgjgzzl-1, fb-msgji0eh-2). "Add arrows
 * and wind direction. Also colour code the strengths like xc weather." "We need
 * wind strength and direction together. Maybe a coloured arrow with a number in a
 * circle at its base." That is a clear brief and it is the right one for a phone.
 *
 * A barb is the chart-table convention and it is genuinely good on paper at a
 * desk: it is precise to five knots and it costs no colour. On a phone at arm's
 * length in daylight, counting feathers is work, and the thing a skipper wants
 * off a glance is "how hard and which way", which an arrow and a number give
 * immediately. The barbs are not deleted, they are one of two styles.
 *
 * THE ARROW POINTS THE WAY THE WIND IS GOING, not where it is from. Every
 * consumer weather map does this (XC Weather, Windy, the Met Office) and it is
 * what people read at a glance. The NUMBER and every readout in the app stay
 * "wind from", which is what a sailor says out loud, so the two never contradict
 * each other: the arrow shows the flow, the words say the bearing it blows from.
 */
import type { Feature } from 'geojson';
import { useSettingsStore } from '@/store/settings';
import { rhumbDestination } from '@/nav';

/** Geometry at scale 1, in nautical miles. Scaled to the grid by the caller. */
const SHAFT_NM = 0.9;
const HEAD_NM = 0.34;
const HEAD_DEG = 145; // how far the head strokes lie back from the shaft

/** Below this there is no meaningful direction to draw. */
export const ARROW_CALM_KN = 2;

/**
 * Wind-speed colour bands, on Beaufort boundaries.
 *
 * Beaufort rather than an even ramp, because the boundaries are the ones that
 * already mean something to a sailor: F4 is a nice day, F6 is a yellow flag at a
 * sailing school, F8 is a gale and the colour should say so without being read.
 * The steps are deliberately banded rather than a smooth gradient, so a glance
 * gives a category and not a shade to interpret.
 *
 * Cold to hot through the useful range, then magenta above storm force so it
 * cannot be mistaken for "just more red".
 */
export const WIND_COLOUR_STEPS: { fromKn: number; colour: string; label: string }[] = [
  { fromKn: 0, colour: '#8fb8d6', label: 'F0-2, light' },
  { fromKn: 7, colour: '#4da3ff', label: 'F3, gentle' },
  { fromKn: 11, colour: '#31c17a', label: 'F4, moderate' },
  { fromKn: 17, colour: '#d8c02e', label: 'F5, fresh' },
  { fromKn: 22, colour: '#e5a53a', label: 'F6, strong' },
  { fromKn: 28, colour: '#e5652b', label: 'F7, near gale' },
  { fromKn: 34, colour: '#e5342b', label: 'F8, gale' },
  { fromKn: 41, colour: '#b03a8a', label: 'F9+, severe' },
];

/** The default anchors of the ladder: where green starts, and where red starts. */
export const DEFAULT_GREEN_FROM_KN = 11;
export const DEFAULT_RED_FROM_KN = 34;

/**
 * Slide a default boundary to the user's thresholds (fb-msmaz0fj-3): the
 * stretch below green scales to the new green start, the middle stretch scales
 * between the new green and red starts, and everything above red keeps its
 * spacing. The colours never change, only where they begin, so a heavier
 * boat's green simply reaches further.
 */
function remapBoundary(kn: number, greenFrom: number, redFrom: number): number {
  if (kn <= DEFAULT_GREEN_FROM_KN) return (kn / DEFAULT_GREEN_FROM_KN) * greenFrom;
  if (kn <= DEFAULT_RED_FROM_KN)
    return (
      greenFrom +
      ((kn - DEFAULT_GREEN_FROM_KN) / (DEFAULT_RED_FROM_KN - DEFAULT_GREEN_FROM_KN)) * (redFrom - greenFrom)
    );
  return kn + (redFrom - DEFAULT_RED_FROM_KN);
}

/**
 * The colour ladder with the user's thresholds applied. This, not the constant
 * above, is what every consumer should read: arrows, wash, legend, and table
 * all shift together or the colours stop meaning one thing.
 */
export function windColourSteps(): { fromKn: number; colour: string; label: string }[] {
  let g = DEFAULT_GREEN_FROM_KN;
  let r = DEFAULT_RED_FROM_KN;
  try {
    const st = useSettingsStore.getState();
    if (Number.isFinite(st.windGreenFromKn)) g = st.windGreenFromKn;
    if (Number.isFinite(st.windRedFromKn)) r = st.windRedFromKn;
  } catch {
    // Tests import this without the store's environment; defaults are honest.
  }
  if (g === DEFAULT_GREEN_FROM_KN && r === DEFAULT_RED_FROM_KN) return WIND_COLOUR_STEPS;
  return WIND_COLOUR_STEPS.map((s) => ({
    ...s,
    fromKn: Math.round(remapBoundary(s.fromKn, g, r) * 10) / 10,
  }));
}

/** The colour for a wind speed, for anything that cannot use a map expression. */
export function windColour(speedKn: number): string {
  const steps = windColourSteps();
  let out = steps[0].colour;
  for (const s of steps) {
    if (speedKn >= s.fromKn) out = s.colour;
  }
  return out;
}

/**
 * A MapLibre `step` expression over `speedKn`, so the colour is computed on the
 * GPU from the feature rather than baked in when the geometry is built. That
 * matters because the geometry is rebuilt on every zoom and every forecast hour,
 * and the colour has no business being rebuilt with it.
 */
export function windColourExpression(): unknown[] {
  const steps = windColourSteps();
  const expr: unknown[] = ['step', ['get', 'speedKn'], steps[0].colour];
  for (const s of steps.slice(1)) {
    expr.push(s.fromKn, s.colour);
  }
  return expr;
}

/**
 * The arrow for one sample: a shaft and a two-stroke head, plus the point at its
 * base where the number sits.
 *
 * MultiLineString rather than two features, so one arrow is one thing to style
 * and one thing to filter.
 */
export function windArrowFeatures(
  position: [number, number],
  dirFromDeg: number,
  speedKn: number,
  scale: number,
): Feature[] {
  const speed = Math.round(speedKn);
  const base: Feature = {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: position },
    properties: { kind: 'arrow-base', speedKn: speed, calm: speedKn < ARROW_CALM_KN },
  };
  if (speedKn < ARROW_CALM_KN) return [base];

  // Blowing TOWARDS: the arrow follows the flow.
  const towards = (dirFromDeg + 180) % 360;
  const shaftNm = SHAFT_NM * scale;
  const headNm = HEAD_NM * scale;

  // Centred on the point, so the arrow does not drift off its own grid cell.
  const tail = rhumbDestination(position, (towards + 180) % 360, shaftNm / 2);
  const tip = rhumbDestination(position, towards, shaftNm / 2);
  const left = rhumbDestination(tip, (towards + HEAD_DEG) % 360, headNm);
  const right = rhumbDestination(tip, (towards - HEAD_DEG + 360) % 360, headNm);

  return [
    {
      type: 'Feature',
      geometry: {
        type: 'MultiLineString',
        coordinates: [
          [tail, tip],
          [left, tip, right],
        ],
      },
      properties: { kind: 'arrow', speedKn: speed },
    },
    base,
  ];
}
